import classNames from 'classnames'
import React from 'react'
import {
  DatePickerStateContext,
  DateSegment,
  Group,
  Label,
  DateInput as ReactAriaDateInput,
  Text,
  TimeFieldStateContext,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import { CalendarToggleButton } from '../Calendar/CalendarToggleButton'
import styles from './DateField.module.less'

interface DateFieldGroupProps {
  isInvalid?: boolean
  label?: string
  children: React.ReactNode
  disabled?: boolean
}

function DateFieldGroup(props: DateFieldGroupProps): React.JSX.Element {
  return (
    <Group className={classNames(styles.DateField, { [styles.DateField_disabled]: props.disabled })}>
      <Label className={classNames(styles.DateField_Label, { [styles.DateField_Label_error]: props.isInvalid })}>
        {props.label}
      </Label>
      {props.children}
    </Group>
  )
}

const DateInput = (): React.JSX.Element => {
  const isLocaleH12 = new Intl.DateTimeFormat(navigator.language, { hour: 'numeric' }).resolvedOptions().hour12

  return (
    <ReactAriaDateInput className={styles.DateField_DateInput}>
      {(segment) => {
        if (!isLocaleH12 && segment.type === 'hour' && (segment.value === 0 || (segment.value && segment.value < 10))) {
          segment.text = segment.text.padStart(2, '0')
        }
        return (
          <DateSegment
            data-testid={`${segment.type}-input`}
            segment={segment}
            className={styles.DateField_DateSegment}
          />
        )
      }}
    </ReactAriaDateInput>
  )
}

interface Props {
  label?: string
  allowEmpty?: boolean
  disabled?: boolean
}

export function CalendarDateField(props: Props): React.JSX.Element {
  const datePickerState = React.useContext(DatePickerStateContext) as any
  const showUntilFurtherNotice = props.allowEmpty && !datePickerState.value
  const { t } = useTranslation()

  return (
    <Tooltip
      title={props.disabled ? '' : t('Select date with picker here')}
      placement="top-start"
      arrow
      display="block"
      PopperProps={{ className: styles.customTooltip }}
    >
      <span>
        <DateFieldGroup label={props.label} isInvalid={datePickerState.isInvalid} disabled={props.disabled}>
          <CalendarToggleButton disabled={props.disabled} />
          {showUntilFurtherNotice ? <UntilFurtherNoticePlaceholer /> : <DateInput />}
        </DateFieldGroup>
      </span>
    </Tooltip>
  )
}

function UntilFurtherNoticePlaceholer(): React.JSX.Element {
  const { t } = useTranslation()
  return <span className={styles.DateField_indefinite}>{t('Until further notice')}</span>
}

interface TimeFieldProps {
  label?: string
  errorMessage?: string
}

export function TimeField(props: TimeFieldProps): React.JSX.Element {
  const timeFieldContext = React.useContext(TimeFieldStateContext) as any
  const isInvalid = timeFieldContext.isInvalid

  return (
    <DateFieldGroup label={props.label} isInvalid={isInvalid}>
      <DateInput />
      {isInvalid && props.errorMessage && (
        // slot="description" is picked up by react-aria-components' TimeField via context and
        // wired into aria-describedby on the field group, regardless of DOM nesting depth.
        <Text slot="description" className={styles.DateField_ScreenReaderOnly}>
          {props.errorMessage}
        </Text>
      )}
    </DateFieldGroup>
  )
}
